<template>
    <div class="border-white border border-solid w-full rounded-2xl bg-slate-50 shadow">
        <div class="bg-amber-50 px-4 py-3 rounded-t-2xl lg:px-8">
            <vx-badge color="amber" size="xs">
                Local <font-awesome-icon widthAuto :icon="faLocationDot"></font-awesome-icon>
            </vx-badge>
        </div>
        <div class="p-4 text-slate-900 lg:p-8">
            <vx-icon-loading v-if="loadingAttributeSets && !attributeSets" spin></vx-icon-loading>
            <template v-else-if="attributeSets">
                <div
                    v-if="!props.expandAll"
                    class="sticky md:static top-0 z-10 bg-slate-50 flex items-center gap-2 mb-6"
                    data-test="profile-switcher"
                >
                    <vx-select
                        size="md"
                        v-model="selectedProfile"
                        :options="profileOptions"
                        data-test="profile-select"
                    ></vx-select>
                    <vx-button
                        type="button"
                        color="muted"
                        outline
                        size="md"
                        data-test="profile-prev"
                        aria-label="Previous profile"
                        :disabled="selectedIndex <= 0"
                        @click="selectProfileAt(selectedIndex - 1)"
                    >
                        <font-awesome-icon widthAuto :icon="faCaretLeft"></font-awesome-icon>
                    </vx-button>
                    <vx-button
                        type="button"
                        color="muted"
                        outline
                        size="md"
                        data-test="profile-next"
                        aria-label="Next profile"
                        :disabled="selectedIndex >= profileEntries.length - 1"
                        @click="selectProfileAt(selectedIndex + 1)"
                    >
                        <font-awesome-icon widthAuto :icon="faCaretRight"></font-awesome-icon>
                    </vx-button>
                </div>

                <div v-for="entry in visibleEntries" :key="entry.id">
                    <div v-if="props.expandAll" class="font-extrabold text-base mb-2 flex items-center gap-1.5">
                        <font-awesome-icon
                            v-if="entry.id === 'corporate'"
                            widthAuto
                            :icon="faCrown"
                            class="text-amber-400"
                        ></font-awesome-icon>
                        <font-awesome-icon v-else-if="entry.isPreferred" widthAuto :icon="faStar"></font-awesome-icon>
                        {{ entry.label }}
                    </div>

                    <div :class="headingClasses">Attributes</div>
                    <div v-if="props.editing && entry.editable" class="flex flex-col gap-4 mb-6">
                        <div data-test="email-wrapper">
                            <vx-label> Email </vx-label>
                            <vx-input
                                size="lg"
                                v-model="entry.model.email"
                                :disabled="props.disabled"
                                :error="displayFirstError(validate[entry.id]?.email)"
                            ></vx-input>
                        </div>

                        <div data-test="entry-channel-wrapper">
                            <vx-label> Entry Channel </vx-label>
                            <vx-input
                                size="lg"
                                v-model="entry.model.entry_channel"
                                :disabled="props.disabled"
                                :error="displayFirstError(validate[entry.id]?.entry_channel)"
                            ></vx-input>
                        </div>

                        <div data-test="membership-status-wrapper">
                            <vx-label> Membership Status </vx-label>
                            <vx-input
                                size="lg"
                                v-model="entry.model.membership_status"
                                :disabled="props.disabled"
                                :error="displayFirstError(validate[entry.id]?.membership_status)"
                            ></vx-input>
                        </div>

                        <div data-test="lifecycle-status-wrapper">
                            <vx-label> Lifecycle Status </vx-label>
                            <vx-input
                                size="lg"
                                v-model="entry.model.lifecycle_status"
                                :disabled="props.disabled"
                                :error="displayFirstError(validate[entry.id]?.lifecycle_status)"
                            ></vx-input>
                        </div>
                    </div>
                    <div v-else class="@container">
                        <div :class="gridClasses">
                            <div :class="gridKeyClasses">Email:</div>
                            <div :class="gridValueClasses">
                                {{ entry.model.email || '-' }}
                            </div>
                            <div :class="gridKeyClasses">Entry Channel:</div>
                            <div :class="gridValueClasses">
                                {{ entry.model.entry_channel || '-' }}
                            </div>
                            <div :class="gridKeyClasses">Membership Status:</div>
                            <div :class="gridValueClasses">
                                {{ entry.model.membership_status || '-' }}
                            </div>
                            <div :class="gridKeyClasses">Lifecycle Status:</div>
                            <div :class="gridValueClasses">
                                {{ entry.model.lifecycle_status || '-' }}
                            </div>
                        </div>
                    </div>

                    <div :class="headingClasses">Custom Attributes</div>
                    <contact-profile-custom-attributes
                        v-if="props.editing && entry.editable"
                        v-model="entry.model.custom_attributes"
                        :disabled="props.disabled"
                        class="mb-6"
                    ></contact-profile-custom-attributes>
                    <template v-else>
                        <vx-alert :closeable="false" v-if="!entry.model.custom_attributes.length" class="text-sm mb-6">
                            No custom attributes were added to this contact.
                        </vx-alert>
                        <div v-else class="@container">
                            <div :class="gridClasses">
                                <template
                                    v-for="customAttribute in entry.model.custom_attributes"
                                    :key="customAttribute.key"
                                >
                                    <div :class="gridKeyClasses">{{ customAttribute.key }}:</div>
                                    <div :class="gridValueClasses">
                                        <a
                                            v-if="isUrl(customAttribute.value)"
                                            :href="customAttribute.value"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="underline text-sky-400"
                                            data-test="url-custom-attribute"
                                        >
                                            {{ customAttribute.value }}
                                        </a>
                                        <template v-else>{{ customAttribute.value }}</template>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <vx-button
                            v-if="props.editableAttributes && entry.editable"
                            color="muted-light"
                            data-test="edit-custom-attributes"
                            size="md"
                            block
                            @click="showCustomAttributesModal = true"
                        >
                            {{ !entry.model.custom_attributes.length ? 'Add' : 'Edit' }}
                            <font-awesome-icon widthAuto :icon="faFilePen" />
                        </vx-button>
                        <contact-custom-attributes-modal
                            v-if="props.editableAttributes && entry.editable"
                            v-model:visible="showCustomAttributesModal"
                            :profileId="entry.id"
                        ></contact-custom-attributes-modal>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faCaretLeft, faCaretRight, faCrown, faFilePen, faLocationDot, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxIconLoading, VxInput, VxLabel, VxSelect } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { email, maxLength } from '@vuelidate/validators';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import ContactCustomAttributesModal from '~/components/contacts/modals/ContactCustomAttributesModal.vue';
import type { ContactGroupAttributeSet, ContactProfileAttributes } from '~/services/contactsClient.types';
import { isUrl } from '~/utils/string';
import { csvInjection, customAttributeValue, displayFirstError } from '~/utils/validation';
import ContactProfileCustomAttributes from './ContactProfileCustomAttributes.vue';
import { useContactDetails } from './useContactDetails';

interface ProfileEntry {
    id: string;
    label: string;
    isPreferred: boolean;
    editable: boolean;
    model: ContactProfileAttributes;
}

interface BroadcastPayload {
    contact?: { id?: number };
}

const props = defineProps({
    editing: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    editableAttributes: {
        type: Boolean,
        default: false,
    },
    expandAll: {
        type: Boolean,
        default: false,
    },
});

const teamId = Spark.state.currentTeam.id;

const { contact, groupId, attributeSets, loadingAttributeSets, getAttributeSets, selectedProfileId } =
    useContactDetails();

const showCustomAttributesModal = ref(false);

const headingClasses = 'font-extrabold text-base mb-2 flex items-center justify-between';
const gridClasses = 'grid grid-cols-1 @xs:grid-cols-[132px_1fr] items-center text-slate-800 text-sm mb-6';
const gridKeyClasses = 'break-all text-xs text-slate-500 @xs:pb-2';
const gridValueClasses = 'break-all pb-2';

const profileEntries = computed<ProfileEntry[]>(() => {
    if (!attributeSets.value) {
        return [];
    }

    return [
        {
            id: 'corporate',
            label: 'Corporate',
            isPreferred: false,
            editable: attributeSets.value.corporate.editable,
            model: attributeSets.value.corporate,
        },
        ...attributeSets.value.groups.map((profile: ContactGroupAttributeSet) => ({
            id: profile.group_id,
            label: profile.group_name || profile.group_id,
            isPreferred: profile.is_preferred,
            editable: profile.editable,
            model: profile,
        })),
    ];
});

const profileOptions = computed(() => profileEntries.value.map((entry) => ({ value: entry.id, label: entry.label })));

const selectedEntry = computed(
    () => profileEntries.value.find((entry) => entry.id === selectedProfileId.value) ?? profileEntries.value[0] ?? null
);

const selectedIndex = computed(() => (selectedEntry.value ? profileEntries.value.indexOf(selectedEntry.value) : -1));

const visibleEntries = computed(() => {
    if (props.expandAll) {
        return profileEntries.value;
    }

    return selectedEntry.value ? [selectedEntry.value] : [];
});

const selectedProfile = computed({
    get: () => selectedEntry.value?.id,
    set: (value) => {
        selectedProfileId.value = value;
    },
});

const selectProfileAt = (index: number) => {
    const entry = profileEntries.value[index];
    if (entry) {
        selectedProfileId.value = entry.id;
    }
};

const profileRules = {
    email: { csvInjection: csvInjection, maxLength: maxLength(191), email: email },
    entry_channel: { csvInjection: csvInjection, maxLength: maxLength(40), format: customAttributeValue },
    membership_status: { csvInjection: csvInjection, maxLength: maxLength(40), format: customAttributeValue },
    lifecycle_status: { csvInjection: csvInjection, maxLength: maxLength(40), format: customAttributeValue },
};

const editableEntries = computed(() => profileEntries.value.filter((entry) => entry.editable));

const validationRules = computed(() =>
    Object.fromEntries(editableEntries.value.map((entry) => [entry.id, profileRules]))
);

const validationModel = computed(() =>
    Object.fromEntries(
        editableEntries.value.map((entry) => [
            entry.id,
            {
                email: entry.model.email,
                entry_channel: entry.model.entry_channel,
                membership_status: entry.model.membership_status,
                lifecycle_status: entry.model.lifecycle_status,
            },
        ])
    )
);

const validate = useVuelidate(validationRules, validationModel);

watch(
    [() => contact.value?.id, () => groupId.value ?? null],
    ([contactId], [previousContactId]) => {
        if (undefined !== previousContactId) {
            showCustomAttributesModal.value = false;
        }
        if (!contactId) {
            return;
        }
        getAttributeSets();
    },
    { immediate: true }
);

const shouldRefreshFromBroadcast = (payload: BroadcastPayload) => {
    if (payload?.contact?.id !== contact.value.id) {
        return false;
    }

    return !props.editing;
};

const refreshFromBroadcast = (payload: BroadcastPayload) => {
    if (!shouldRefreshFromBroadcast(payload)) {
        return;
    }

    getAttributeSets();
};

const broadcastEvents = [
    'ContactDetailsUpdated',
    'LocalContactAttributesUpdated',
    'CustomAttributeCreated',
    'CustomAttributeUpdated',
    'CustomAttributeDeleted',
    'LocalCustomAttributeCreated',
    'LocalCustomAttributeUpdated',
    'LocalCustomAttributeDeleted',
];

let echoChannel: EchoChannel | undefined;

onMounted(() => {
    echoChannel = window.Echo.private(`contacts.${teamId}`);
    broadcastEvents.forEach((event) => echoChannel?.listen(event, refreshFromBroadcast));
});

onUnmounted(() => {
    broadcastEvents.forEach((event) => echoChannel?.stopListening(event, refreshFromBroadcast));
});
</script>
